import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createTag, CreateTagRequest, updateTag, UpdateTagRequest, getTag, generateTagNameEnAndSlug } from '../../shared/api/tags';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminTagsPath } from '../../shared/config/env';
import { generateTagNameEnAndSlugPrompt } from '../../shared/prompts/generateTags';
import AdminSidebar from '../components/AdminSidebar';
import './TagCreate.css';

function TagCreate() {
  const { id: tagId } = useParams<{ id?: string }>();
  const isEditMode = tagId !== undefined;
  
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [slug, setSlug] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminTagsPath = getAdminTagsPath();

  // 編集モード時、既存タグデータを読み込む
  useEffect(() => {
    if (isEditMode && tagId) {
      const fetchTag = async () => {
        try {
          setIsLoading(true);
          setError(null);
          const response = await getTag(tagId);
          const tag = response.tag;
          setName(tag.name);
          setNameEn(tag.nameEn || '');
          setSlug(tag.slug);
          setIsVisible(tag.isVisible);
        } catch (err) {
          if (err instanceof Error && (err as any).isAuthError) {
            logout();
            navigate(adminLoginPath, { replace: true });
            return;
          }
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('タグの取得に失敗しました');
          }
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchTag();
    }
  }, [isEditMode, tagId, logout, navigate, adminLoginPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // バリデーション
    if (!name.trim()) {
      setError('名前（日本語）は必須です');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // 編集モード
        if (!tagId) {
          setError('タグIDが取得できませんでした');
          setIsSubmitting(false);
          return;
        }
        
        const tagData: UpdateTagRequest = {
          name: name.trim(),
          ...(nameEn.trim() && { nameEn: nameEn.trim() }),
          ...(slug.trim() && { slug: slug.trim() }),
          isVisible,
          // usageCountは送信しない（Post DynamoDBから動的に取得されるため）
        };

        await updateTag(tagId, tagData);
        navigate(adminTagsPath);
      } else {
        // 作成モード
        const tagData: CreateTagRequest = {
          name: name.trim(),
          ...(nameEn.trim() && { nameEn: nameEn.trim() }),
          ...(slug.trim() && { slug: slug.trim() }),
          isVisible,
          // usageCountは送信しない（Post DynamoDBから動的に取得されるため）
        };

        await createTag(tagData);
        navigate(adminTagsPath);
      }
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(isEditMode ? 'タグの更新に失敗しました' : 'タグの作成に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(adminTagsPath);
  };

  /**
   * 日本語タグ名から英語名とSlugをAI生成する
   */
  const handleGenerateNameEnAndSlug = async () => {
    if (!name.trim()) {
      setError('名前（日本語）を入力してください');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // プロンプトを生成
      const prompt = generateTagNameEnAndSlugPrompt({
        tagName: name.trim(),
      });

      // 英語名とSlugを生成
      const response = await generateTagNameEnAndSlug({
        tagName: name.trim(),
        prompt: prompt,
      });

      // 生成された値を設定
      setNameEn(response.nameEn);
      setSlug(response.slug);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('英語名とSlugの生成に失敗しました');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-layout__main">
        <div className="tag-create">
          <header className="tag-create__header">
            <h1 className="tag-create__title">
              {isEditMode ? 'タグ編集' : 'タグ新規作成'}
            </h1>
            <div className="tag-create__actions">
              <button
                type="button"
                onClick={handleCancel}
                className="tag-create__button tag-create__button--secondary"
                disabled={isSubmitting || isLoading}
              >
                キャンセル
              </button>
              <button
                type="submit"
                form="tag-form"
                className="tag-create__button tag-create__button--primary"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting ? '保存中...' : '保存'}
              </button>
            </div>
          </header>
          <main className="tag-create__content">
            {isLoading ? (
              <p className="tag-create__loading">読み込み中...</p>
            ) : (
              <>
                {error && (
                  <div className="tag-create__error">
                    {error}
                  </div>
                )}
                <form id="tag-form" onSubmit={handleSubmit} className="tag-create__form">
                  <div className="tag-create__form-group">
                    <label htmlFor="name" className="tag-create__label">
                      名前（日本語） <span className="tag-create__required">*</span>
                      <button
                        type="button"
                        onClick={handleGenerateNameEnAndSlug}
                        disabled={isSubmitting || isLoading || isGenerating || !name.trim()}
                        className="tag-create__generate-button"
                        style={{
                          marginLeft: '8px',
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {isGenerating ? '生成中...' : 'AI生成'}
                      </button>
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="例: Python"
                      disabled={isSubmitting || isLoading}
                      className="tag-create__input"
                      required
                    />
                    <small className="tag-create__help">
                      日本語名を入力後、「AI生成」ボタンをクリックすると、英語名とSlugが自動生成されます
                    </small>
                  </div>

                  <div className="tag-create__form-group">
                    <label htmlFor="nameEn" className="tag-create__label">
                      名前（英語）
                    </label>
                    <input
                      type="text"
                      id="nameEn"
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                      placeholder="例: Python"
                      disabled={isSubmitting || isLoading || isGenerating}
                      className="tag-create__input"
                    />
                    <small className="tag-create__help">
                      AI生成で自動入力されます。手動で編集も可能です
                    </small>
                  </div>

                  <div className="tag-create__form-group">
                    <label htmlFor="slug" className="tag-create__label">
                      スラッグ
                    </label>
                    <input
                      type="text"
                      id="slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="例: python"
                      disabled={isSubmitting || isLoading || isGenerating}
                      className="tag-create__input"
                    />
                    <small className="tag-create__help">
                      AI生成で自動入力されます。手動で編集も可能です（英語名をベースに生成）
                    </small>
                  </div>

                  <div className="tag-create__form-group">
                    <label htmlFor="isVisible" className="tag-create__label">
                      <input
                        type="checkbox"
                        id="isVisible"
                        checked={isVisible}
                        onChange={(e) => setIsVisible(e.target.checked)}
                        disabled={isSubmitting || isLoading}
                        className="tag-create__checkbox"
                      />
                      表示する
                    </label>
                    <small className="tag-create__help">
                      チェックを外すと、Home画面のタグ一覧に表示されません
                    </small>
                  </div>
                </form>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default TagCreate;

